import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { TPersonalLicensesDTO, TPersonalLicense, LicensesType, LicenseName } from '@/core/infra';
import { PersonalLicenseDetails } from './PersonalLicenseDetails';
import Icon from '../common/Icon';
import { getLicenses } from './service';
import { wrap, wrapEvent } from '../../helper';
import { useContextDispatch } from '../context';
import CryptoIcon from '../../public/img/license/crypto-icon.svg';
import FullIcon from '../../public/img/license/full-icon.svg';
import PlayerIcon from '../../public/img/license/player-icon.svg';

// const checkExpirationDate = ({ active, days, licenseType, expirationDate }, t) => {
//   if (licenseType === 'CRYPTO') {
//     return t('date_days_crypto');
//   }
//   if (active) {
//     const momentExpiration = moment(expirationDate);
//     const momentCurrent = moment();
//     const expirationDaysDiff = momentExpiration.diff(momentCurrent, 'days');
//     return `${expirationDaysDiff} ${declension(expirationDaysDiff, t('date_days'))}`;
//   }
//   return `${days} ${declension(days, t('date_days'))}`;
// };

// eslint-disable-next-line no-shadow
enum Filters {
  All = 'ALL',
  Archive = 'ARCHIVE',
}

const getTypeLicence = ({ commerceType, licenseType }) => {
  if (commerceType === 'TRIAL' || commerceType === 'FREE') {
    return `${LicenseName[licenseType]} (${commerceType})`;
  }
  return `${LicenseName[licenseType]}`;
};

const PersonalLicensesComponent = () => {
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState<TPersonalLicensesDTO>([]);
  const [licensesOffset, setLicensesOffset] = useState(0);
  const [isLoadMore, setIsLoadMore] = useState(true);
  const {
    query: { filter },
  } = useRouter() as any;
  /* const {
    user: { email },
  } = useContextState();*/
  const [activeFilter, setActiveFilter] = useState<string>(filter || 'ALL');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { notificationShowAction } = useContextDispatch();

  /* const licenseRenewEvent = useThrottle(
    () => AmplitudeSendEvent({ event_type: `LicenseRenew`, email }),
    2000
  );*/

  const closeFilterModal = ({ target }) => {
    if (target && target.id !== 'filters-modal') {
      setIsFilterOpen(false);
    }
  };

  const loadMoreLicenses = () => {
    setLicensesOffset((prevOffset) => prevOffset + 10);
  };

  const renderIcon = ({ licenseType }) => {
    switch (licenseType as LicensesType) {
      case LicensesType.CRYPTO:
        return <CryptoIcon />;
      case LicensesType.PLAYER:
        return <PlayerIcon />;
      case LicensesType.STOCK_MARKET:
        return <FullIcon />;
      default:
        return <FullIcon />;
    }
  };

  /* const isShowRenewButton = (status: string, expirationDate: string) => {
    const currentDate = moment(new Date());
    const expDate = moment(expirationDate);
    const difference = moment.duration(expDate.diff(currentDate));
    const days = Math.ceil(difference.asDays());
    return status === 'EXPIRED' || days <= 7;
  };*/

  useEffect(() => {
    getLicenses(activeFilter, 10, 0).then((newLicenses: TPersonalLicensesDTO) => {
      setLicenses(newLicenses);
      setLicensesOffset(0);
      setIsLoadMore(true);
    });
  }, [activeFilter]);

  useEffect(() => {
    if (licensesOffset) {
      getLicenses(activeFilter, 10, licensesOffset).then((newLicenses: TPersonalLicensesDTO) => {
        if (!newLicenses.length) {
          setIsLoadMore(false);
        }
        setLicenses((currLicenses) => currLicenses.concat(newLicenses));
      });
    }
  }, [activeFilter, licensesOffset]);

  useEffect(() => {
    document.addEventListener('click', closeFilterModal);

    return () => {
      document.removeEventListener('click', closeFilterModal);
    };
  }, []);

  const renderNoLicenses = () => {
    return (
      <p className="no-licenses">
        {activeFilter === Filters.All ? t('no-licenses') : t('no-expired-licenses')}
      </p>
    );
  };

  const getAction = (license: TPersonalLicense) => {
    const { commerceType, licenseStatus, productId, licenseType, licenseId } = license;

    if (commerceType === 'PAID' && productId !== 6) {
      return (
        <Link
          href={`/terminal/confirm-buying-license/${licenseType.toLowerCase()}?licenseId=${licenseId}`}
          passHref
        >
          <a className="btn">
            {licenseStatus === 'EXPIRED'
              ? t('personal-licenses_renew')
              : t('personal-licenses_extend')}
          </a>
        </Link>
      );
    }

    return null;
  };

  return (
    <div className="page-content wide">
      <div className="licenses-header">
        <h1>{t('licenses')}</h1>
        <div className="toggle licenses-toggle" onClick={wrapEvent(setIsFilterOpen, !isFilterOpen)}>
          {t('show')}: <span className="licenses-filter">{t(activeFilter)}</span>
          <div className={classNames('arr', isFilterOpen ? 'toggle-rotated' : '')}>
            <Icon name="20-darr" file="sprite" width="12" height="12" />
          </div>
          {isFilterOpen && (
            <div id="licence-filters" className="filters">
              <div
                className={classNames(
                  'filter',
                  activeFilter === Filters.All ? 'filter-active' : ''
                )}
                onClick={() => setActiveFilter(Filters.All)}
              >
                {t(Filters.All)}
              </div>
              <div
                className={classNames(
                  'filter',
                  activeFilter === Filters.Archive ? 'filter-active' : ''
                )}
                onClick={() => setActiveFilter(Filters.Archive)}
              >
                {t(Filters.Archive)}
              </div>
            </div>
          )}
        </div>
      </div>
      {licenses?.length ? (
        <InfiniteScroll
          dataLength={licenses.length}
          hasMore={isLoadMore}
          loader={null}
          next={loadMoreLicenses}
        >
          <div className="licenses-grid">
            {licenses.map((license) => (
              <div key={license.licenseId} className="licenses-grid__item">
                <div className="licenses-grid__item__top">
                  <div className="icon">{renderIcon(license)}</div>
                  <div>{getAction(license)}</div>
                </div>
                <div className="licenses-grid__item__heading">{getTypeLicence(license)}</div>
                <PersonalLicenseDetails license={license} />
                <div className="licenses-grid__item__creds">
                  <div className="licenses-grid__item__cred">
                    <div>
                      <div className="name">{t('personal-licenses_form_login')}</div>
                      <div className="txt notranslate imt-notranslate" translate="no">
                        {license.login}
                      </div>
                    </div>
                    <CopyToClipboard
                      text={license.login}
                      onCopy={wrap(notificationShowAction, { text: t('copied') })}
                    >
                      <button className="btn copy">
                        <Icon file="sprite" name="30-copy" width={24} height={24} />
                      </button>
                    </CopyToClipboard>
                  </div>
                  <div className="licenses-grid__item__cred">
                    <div>
                      <div className="name">{t('personal-licenses_form_password')}</div>
                      <div className="txt notranslate imt-notranslate" translate="no">
                        {license.password}
                      </div>
                    </div>
                    <CopyToClipboard
                      text={license.password}
                      onCopy={wrap(notificationShowAction, { text: t('copied') })}
                    >
                      <button className="btn copy">
                        <Icon file="sprite" name="30-copy" width={24} height={24} />
                      </button>
                    </CopyToClipboard>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </InfiniteScroll>
      ) : (
        renderNoLicenses()
      )}
    </div>
  );
};

export default PersonalLicensesComponent;
